import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Phone, PhoneOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import axiosInstance from '../../config/axiosConfig';
import eventBus from '../../utils/eventBus'; // Task 34
import toast from '../../utils/toast'; // Task 34

/**
 * CallButtonMobile - Touch-optimized call button with native dialer invocation
 * 
 * Task 32: Call Button Activation & Deep-Link Consistency
 * 
 * Features:
 * - Direct tel: scheme invocation within user gesture
 * - Backend call logging with idempotency
 * - 1.5s debounce protection
 * - Toast notifications for feedback
 * - 56px minimum touch target (WCAG AA)
 * - Cross-browser support (Chrome/Safari)
 * - Haptic feedback on mobile devices
 * 
 * Generated by Windsurf Full-Stack Intelligence — Task 32 Deep-Link Enablement
 */

const CallButtonMobile = ({ 
  candidate,
  jdId = null,
  onCallStart = null,
  onCallEnd = null,
  disabled = false,
  size = 'large', // 'small', 'medium', 'large'
  className = ''
}) => {
  // State management
  const [isPressed, setIsPressed] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [lastCallLogId, setLastCallLogId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info'); // 'info', 'success', 'error'
  
  const debounceTimerRef = useRef(null);
  const toastTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Trigger haptic feedback (if supported)
  const triggerHaptic = useCallback((pattern = [10, 50, 10]) => {
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        // Silently fail if vibrate not supported
        console.debug('[CALL_MOBILE] Vibrate not supported:', error);
      }
    }
  }, []);

  // Show toast notification
  const displayToast = useCallback((message, type = 'info', duration = 3000) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);

    // Auto-hide toast after duration
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
    }, duration);
  }, []);

  // Check browser support for tel: scheme
  const checkTelSupport = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isChrome = /chrome|crios/i.test(userAgent);
    const isSafari = /safari/i.test(userAgent) && !/chrome/i.test(userAgent);
    
    return isMobile || isChrome || isSafari;
  }, []);

  // Log call initiation to backend
  const logCallInitiation = useCallback(async (phoneNumber) => {
    try {
      const response = await axiosInstance.post(
        '/call/mobile/initiate',
        {
          candidate_id: candidate.id,
          jd_id: jdId,
          phone_number: phoneNumber,
          source: 'CallButtonMobile'
        },
        {
          headers: {
            'X-Spes-App-Client': 'PWA',
            'X-Spes-App-Version': 'v32.3'
          }
        }
      );

      if (response.data.success) {
        setLastCallLogId(response.data.call_log_id);
        
        if (response.data.is_duplicate) {
          console.info('[CALL_MOBILE] Duplicate call prevented');
        } else {
          console.info(`[CALL_MOBILE] Call logged: ID=${response.data.call_log_id}`);
        }
        
        // Task 34: Emit analytics event
        eventBus.emit('call_initiated', {
          candidate_id: candidate.id,
          jd_id: jdId,
          source: 'CallButtonMobile',
          metadata: {
            phone: phoneNumber,
            candidate_name: candidate.name,
            call_log_id: response.data.call_log_id
          }
        });
        
        return response.data.call_log_id;
      }
    } catch (error) {
      console.error('[CALL_MOBILE] Failed to log call initiation:', error);
      // Don't block the call if logging fails
      displayToast('Call logging failed, but proceeding with call', 'error');
    }
    
    return null;
  }, [candidate, jdId, displayToast]);

  // Invoke native dialer with tel: scheme
  const invokeDialer = useCallback((phoneNumber) => {
    try {
      // Create dynamic anchor element within user gesture context
      const link = document.createElement('a');
      link.href = `tel:${phoneNumber}`;
      link.style.display = 'none';
      
      // Append to DOM to ensure it's part of the document
      document.body.appendChild(link);
      
      // Trigger click synchronously (critical for iOS Safari)
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      console.info(`[CALL_MOBILE] Dialer invoked for: ${phoneNumber}`);
      displayToast('Opening dialer...', 'info', 1500);
      
      return true;
    } catch (error) {
      console.error('[CALL_MOBILE] Failed to invoke dialer:', error);
      displayToast('Failed to open dialer', 'error');
      return false;
    }
  }, [displayToast]);

  // Main call handler (MUST execute synchronously within user gesture)
  const handleCall = useCallback(() => {
    // Validation checks
    if (disabled || isDebouncing) {
      console.warn('[CALL_MOBILE] Call blocked:', { disabled, isDebouncing });
      return;
    }

    if (!candidate || !candidate.phone) {
      displayToast('No phone number available', 'error');
      return;
    }

    // Check browser support
    if (!checkTelSupport()) {
      displayToast('Use Chrome or Safari to dial directly', 'error');
      return;
    }

    // Trigger haptic feedback
    triggerHaptic([50, 100, 50]);

    // Set debounce flag
    setIsDebouncing(true);
    
    // Log to backend (fire-and-forget, don't block dialer)
    logCallInitiation(candidate.phone);
    
    // Invoke dialer immediately (must be synchronous!)
    const success = invokeDialer(candidate.phone);
    
    // Call parent callback if provided
    if (success && onCallStart) {
      try {
        onCallStart(candidate);
      } catch (error) {
        console.error('[CALL_MOBILE] onCallStart callback error:', error);
      }
    }

    // Reset debounce after 1.5 seconds
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setIsDebouncing(false);
    }, 1500);
  }, [
    disabled,
    isDebouncing,
    candidate,
    checkTelSupport,
    displayToast,
    triggerHaptic,
    logCallInitiation,
    invokeDialer,
    onCallStart
  ]);

  // Handle press start (visual feedback)
  const handlePressStart = useCallback(() => {
    if (disabled || isDebouncing) return;
    setIsPressed(true);
    triggerHaptic([10]);
  }, [disabled, isDebouncing, triggerHaptic]);

  // Handle press end (visual feedback)
  const handlePressEnd = useCallback(() => {
    setIsPressed(false);
  }, []);

  // Get button size classes
  const getSizeClasses = () => {
    const sizeMap = {
      small: 'w-12 h-12',
      medium: 'w-14 h-14',
      large: 'w-16 h-16',
    };
    return sizeMap[size] || sizeMap.large;
  };

  // Base button classes
  const baseButtonClasses = `
    ${getSizeClasses()}
    ${isDebouncing ? 'bg-gray-300 dark:bg-gray-700' : 'bg-success hover:bg-success-dark'}
    ${isPressed && !isDebouncing ? 'scale-95 shadow-inner' : 'scale-100'}
    text-white
    rounded-full flex items-center justify-center
    transition-all duration-200 cursor-pointer
    shadow-lg hover:shadow-xl
    focus:outline-none focus:ring-4 focus:ring-success/30
    disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
    ${!disabled && !isDebouncing ? 'active:scale-95' : ''}
    ${className}
  `.trim();

  // Toast styling based on type
  const getToastClasses = () => {
    const typeMap = {
      info: 'bg-primary text-white',
      success: 'bg-success text-white',
      error: 'bg-danger text-white',
    };
    return typeMap[toastType] || typeMap.info;
  };

  return (
    <>
      {/* Call Button */}
      <button
        className={baseButtonClasses}
        onClick={handleCall}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        disabled={disabled || isDebouncing}
        aria-label={`Call ${candidate?.name || 'candidate'}`}
        aria-disabled={disabled || isDebouncing}
      >
        {isDebouncing ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <Phone className="w-6 h-6" />
        )}
      </button>

      {/* Toast Notification */}
      {showToast && (
        <div
          className={`
            fixed bottom-24 left-1/2 transform -translate-x-1/2
            px-4 py-2 rounded-full
            ${getToastClasses()}
            shadow-lg
            flex items-center space-x-2
            animate-slide-up
            z-50
          `}
          role="alert"
          aria-live="polite"
        >
          {toastType === 'success' && <CheckCircle className="w-4 h-4" />}
          {toastType === 'error' && <AlertCircle className="w-4 h-4" />}
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}
    </>
  );
};

export default React.memo(CallButtonMobile);
